// LearnCore Frontend - 编辑学习集页面

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import Loading from '../components/Loading';
import { InlineLoading } from '../components/Loading';
import ImageInput from '../components/ImageInput';
import TagEditor, { clearTagCache } from '../components/TagEditor';
import type { StudySetDetails, Flashcard, Tag } from '../types';
import { 
  PlusIcon, 
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowLeftIcon,
  DocumentTextIcon,
  CheckIcon,
  XMarkIcon,
  CloudArrowUpIcon,
  ExclamationTriangleIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';

interface FlashcardInput {
  id?: string;
  term: string;
  definition: string;
  term_image_url?: string;
  definition_image_url?: string;
  tags?: Tag[];
}

export default function EditSetPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [studySetDetails, setStudySetDetails] = useState<StudySetDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // 学习集基本信息状态
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    is_public: true
  });
  const [infoSaving, setInfoSaving] = useState(false);
  const [infoChanged, setInfoChanged] = useState(false);
  const [infoSaveSuccess, setInfoSaveSuccess] = useState(false);
  
  // 卡片管理状态
  const [flashcards, setFlashcards] = useState<(Flashcard & { isNew?: boolean; hasChanges?: boolean; saving?: boolean; showImages?: boolean })[]>([]);
  
  // 标签相关状态
  const [currentTags, setCurrentTags] = useState<Tag[]>([]);
  
  // 批量导入相关状态
  const [showBatchImport, setShowBatchImport] = useState(false);
  const [batchText, setBatchText] = useState('');
  const [batchImportError, setBatchImportError] = useState<string | null>(null);
  const [batchImporting, setBatchImporting] = useState(false);
  // 批量导入确认与待处理数据
  const [pendingImportCards, setPendingImportCards] = useState<FlashcardInput[] | null>(null);
  const [pendingImportCount, setPendingImportCount] = useState<number | null>(null);
  const [missingTags, setMissingTags] = useState<string[]>([]);
  const [creatingTags, setCreatingTags] = useState<Set<string>>(new Set());
  
  // 自动保存定时器和输入法状态
  const infoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isComposingRef = useRef(false);

  useEffect(() => {
    if (id) {
      loadStudySet(id);
    }
  }, [id]);

  const loadStudySet = async (setId: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.getStudySet(setId);
      
      if (!response.is_owner) {
        setError('您没有权限编辑此学习集');
        return;
      }
      
      setStudySetDetails(response);
      setFormData({
        title: response.set.title,
        description: response.set.description || '',
        is_public: response.set.is_public === 1
      });
      
      setFlashcards(response.set.flashcards.map(card => ({
        ...card,
        tags: card.tags || [],
        isNew: false,
        hasChanges: false,
        saving: false,
        showImages: Boolean(card.term_image_url || card.definition_image_url)
      })));
      
      setInfoChanged(false);
      // 初始化标签
      const detailTags = (response as any).tags as Tag[] | undefined;
      setCurrentTags(detailTags && Array.isArray(detailTags) ? detailTags : []);
      
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError('学习集不存在');
      } else if (err.response?.status === 403) {
        setError('无权访问此学习集');
      } else {
        setError(err.response?.data?.error || '获取学习集失败');
      }
    } finally {
      setLoading(false);
    }
  };

  // 标签变化处理
  const handleTagsChange = async () => {
    // TagEditor 组件内部已经处理保存，这里只需更新本地状态
    await loadStudySet(id!);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
    
    setInfoChanged(true);
    setInfoSaveSuccess(false);
    
    // 清除之前的定时器
    if (infoSaveTimeoutRef.current) {
      clearTimeout(infoSaveTimeoutRef.current);
    }
    
    // 只有在非输入法组合状态下才启动自动保存定时器
    if (!isComposingRef.current) {
      infoSaveTimeoutRef.current = setTimeout(() => {
        autoSaveInfo();
      }, 5000); // 5秒后自动保存
    }
  };

  // 处理输入法开始组合
  const handleCompositionStart = () => {
    isComposingRef.current = true;
    // 清除可能存在的自动保存定时器
    if (infoSaveTimeoutRef.current) {
      clearTimeout(infoSaveTimeoutRef.current);
      infoSaveTimeoutRef.current = null;
    }
  };

  // 处理输入法结束组合
  const handleCompositionEnd = () => {
    isComposingRef.current = false;
    // 输入法完成后，启动自动保存定时器
    if (infoChanged && infoSaveTimeoutRef.current === null) {
      infoSaveTimeoutRef.current = setTimeout(() => {
        autoSaveInfo();
      }, 5000); // 5秒后自动保存
    }
  };

  // 自动保存学习集信息
  const autoSaveInfo = async () => {
    if (!id || !infoChanged) return;
    
    try {
      setInfoSaving(true);
      await apiClient.updateStudySetInfo(id, formData);
      setInfoChanged(false);
      setInfoSaveSuccess(true);
      
      // 3秒后隐藏成功提示
      setTimeout(() => setInfoSaveSuccess(false), 3000);
    } catch (err: any) {
      console.error('自动保存失败:', err);
      // 失败时不显示错误，等待手动保存
    } finally {
      setInfoSaving(false);
    }
  };

  // 手动保存学习集信息
  const handleSaveInfo = async () => {
    if (!id) return;
    
    try {
      setInfoSaving(true);
      await apiClient.updateStudySetInfo(id, formData);
      setInfoChanged(false);
      setInfoSaveSuccess(true);
      
      setTimeout(() => setInfoSaveSuccess(false), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || '保存学习集信息失败');
    } finally {
      setInfoSaving(false);
    }
  };

  const handleFlashcardChange = (index: number, field: 'term' | 'definition' | 'term_image_url' | 'definition_image_url' | 'tags', value: string | undefined | Tag[]) => {
    setFlashcards(prev => prev.map((card, i) => 
      i === index ? { ...card, [field]: value, hasChanges: true } : card
    ));
  };

  const addFlashcard = () => {
    const newCard = { 
      id: `temp_${Date.now()}`, // 临时ID
      set_id: id || '', // 添加必需的set_id字段
      term: '', 
      definition: '', 
      term_image_url: undefined, 
      definition_image_url: undefined,
      sort_order: flashcards.length,
      tags: [],
      isNew: true,
      hasChanges: false,
      saving: false,
      showImages: false
    };
    // 追加到末尾（渲染时逆序显示，保证新卡片位于顶部）
    setFlashcards(prev => [...prev, newCard]);
  };

  const removeFlashcard = async (index: number) => {
    const card = flashcards[index];
    
    if (flashcards.length <= 1) {
      alert('至少需要保留1张卡片');
      return;
    }

    if (card.isNew) {
      // 新卡片直接从列表中移除
      setFlashcards(prev => prev.filter((_, i) => i !== index));
    } else {
      // 现有卡片需要调用API删除
      if (!id || !card.id) return;
      
      try {
        await apiClient.deleteCard(id, card.id);
        setFlashcards(prev => prev.filter((_, i) => i !== index));
      } catch (err: any) {
        setError(err.response?.data?.error || '删除卡片失败');
      }
    }
  };

  // 保存单个卡片
  const saveCard = async (index: number) => {
    const card = flashcards[index];
    if (!id || !card.term.trim() || !card.definition.trim()) return;

    try {
      setFlashcards(prev => prev.map((c, i) => 
        i === index ? { ...c, saving: true } : c
      ));

      if (card.isNew) {
        // 创建新卡片
        const response = await apiClient.createCard(id, {
          term: card.term.trim(),
          definition: card.definition.trim(),
          term_image_url: card.term_image_url,
          definition_image_url: card.definition_image_url,
          sort_order: card.sort_order,
          tag_ids: card.tags?.map(tag => tag.id) || []
        });
        
        if (response.card) {
          setFlashcards(prev => prev.map((c, i) => 
            i === index ? { 
              ...response.card, 
              tags: response.card.tags || [],
              isNew: false, 
              hasChanges: false, 
              saving: false 
            } : c
          ));
        }
      } else if (card.hasChanges) {
        // 更新现有卡片
        await apiClient.updateCard(id, card.id, {
          term: card.term.trim(),
          definition: card.definition.trim(),
          term_image_url: card.term_image_url,
          definition_image_url: card.definition_image_url,
          tag_ids: card.tags?.map(tag => tag.id) || []
        });
        
        setFlashcards(prev => prev.map((c, i) => 
          i === index ? { ...c, hasChanges: false, saving: false } : c
        ));
      }
    } catch (err: any) {
      setError(err.response?.data?.error || '保存卡片失败');
      setFlashcards(prev => prev.map((c, i) => 
        i === index ? { ...c, saving: false } : c
      ));
    }
  };

  // 批量保存所有有变更的卡片
  const saveAllCards = async () => {
    if (!id) return;

    const changedCards = flashcards.filter(card => card.hasChanges && !card.isNew);
    const newCards = flashcards.filter(card => card.isNew && card.term.trim() && card.definition.trim());

    try {
      // 批量更新现有卡片
      if (changedCards.length > 0) {
        await apiClient.updateCardsBatch(id, {
          cards: changedCards.map(card => ({
            id: card.id,
            term: card.term.trim(),
            definition: card.definition.trim(),
            term_image_url: card.term_image_url,
            definition_image_url: card.definition_image_url,
            tag_ids: card.tags?.map(tag => tag.id) || []
          }))
        });
      }

      // 批量创建新卡片
      if (newCards.length > 0) {
        await apiClient.createCardsBatch(id, {
          cards: newCards.map(card => ({
            term: card.term.trim(),
            definition: card.definition.trim(),
            term_image_url: card.term_image_url,
            definition_image_url: card.definition_image_url,
            sort_order: card.sort_order,
            tag_ids: card.tags?.map(tag => tag.id) || []
          }))
        });
      }

      // 重新加载数据
      await loadStudySet(id);
      
    } catch (err: any) {
      setError(err.response?.data?.error || '批量保存失败');
    }
  };

  // 解析 Markdown 表格
  const parseMarkdownTable = (text: string): FlashcardInput[] => {
    const lines = text.trim().split('\n').map(line => line.trim()).filter(line => line);
    
    if (lines.length < 3) {
      throw new Error('Markdown 表格至少需要 3 行（标题行、分隔符行、数据行）');
    }

    // 提取 Markdown 图片：![alt](url)
    const extractImageFromMarkdown = (input: string): { text: string; imageUrl?: string } => {
      const imageRegex = /!\[(.*?)\]\((.*?)\)/;
      const match = input.match(imageRegex);
      if (!match) {
        return { text: input.trim() };
      }
      const alt = match[1]?.trim() || '';
      const url = match[2]?.trim();
      const textWithoutImage = input.replace(match[0], '').trim();
      // 若移除图片后文本为空，则回退到 alt 作为文本
      const text = textWithoutImage || alt;
      return { text, imageUrl: url };
    };

    // 解析表头
    const headerLine = lines[0];
    if (!headerLine.includes('|')) {
      throw new Error('第一行必须是表格标题行');
    }
    
    const headers = headerLine.split('|').map(cell => cell.trim()).filter(cell => cell);
    
    if (headers.length < 2) {
      throw new Error('表格至少需要两列');
    }

    // 检查分隔符行
    const separatorLine = lines[1];
    if (!separatorLine.includes('|') || !separatorLine.includes('-')) {
      throw new Error('第二行必须是表格分隔符（例如：| --- | --- |）');
    }

    // 智能识别术语和定义列
    let termColumnIndex = -1;
    let definitionColumnIndex = -1;
    // 可选：术语图片与定义图片列
    let termImageColumnIndex = -1;
    let definitionImageColumnIndex = -1;
    // 可选：标签列
    let tagColumnIndex = -1;

    // 可能的术语列名称
    const termKeywords = ['术语', '单词', '词汇', 'term', 'word', 'vocabulary', '题目', '问题', 'question'];
    // 可能的定义列名称  
    const definitionKeywords = ['定义', '解释', '答案', 'definition', 'explanation', 'answer', '含义', 'meaning', '翻译', 'translation'];

    // 查找术语列
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i].toLowerCase();
      if (termKeywords.some(keyword => header.includes(keyword.toLowerCase()))) {
        termColumnIndex = i;
        break;
      }
    }

    // 查找定义列
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i].toLowerCase();
      if (definitionKeywords.some(keyword => header.includes(keyword.toLowerCase()))) {
        definitionColumnIndex = i;
        break;
      }
    }

    // 查找术语图片与定义图片列（可选）
    const termImageKeywords = ['术语图片', '术语图', 'term image', 'term_image', 'term picture', 'term pic', 'term_img'];
    const definitionImageKeywords = ['定义图片', '定义图', 'definition image', 'definition_image', 'definition picture', 'definition pic', 'definition_img', 'answer image', 'answer_image'];
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i].toLowerCase();
      if (termImageColumnIndex === -1 && termImageKeywords.some(keyword => header.includes(keyword.toLowerCase()))) {
        termImageColumnIndex = i;
      }
      if (definitionImageColumnIndex === -1 && definitionImageKeywords.some(keyword => header.includes(keyword.toLowerCase()))) {
        definitionImageColumnIndex = i;
      }
    }

    // 查找标签列（可选）
    const tagKeywords = ['标签', 'tag', 'tags', '分类', 'category', 'categories'];
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i].toLowerCase();
      if (tagKeywords.some(keyword => header.includes(keyword.toLowerCase()))) {
        tagColumnIndex = i;
        break;
      }
    }

    // 如果没有找到匹配的列名，使用默认策略
    if (termColumnIndex === -1 && definitionColumnIndex === -1) {
      // 默认第一列为术语，第二列为定义
      termColumnIndex = 0;
      definitionColumnIndex = 1;
    } else if (termColumnIndex === -1) {
      // 找到了定义列但没找到术语列，术语列为另一列
      termColumnIndex = definitionColumnIndex === 0 ? 1 : 0;
    } else if (definitionColumnIndex === -1) {
      // 找到了术语列但没找到定义列，定义列为另一列
      definitionColumnIndex = termColumnIndex === 0 ? 1 : 0;
    }

    const result: FlashcardInput[] = [];
    
    // 从第三行开始解析数据
    for (let i = 2; i < lines.length; i++) {
      const line = lines[i];
      if (!line.includes('|')) continue;
      
      // 解析表格行
      const cells = line.split('|').map(cell => cell.trim()).filter(cell => cell);
      
      if (cells.length >= Math.max(termColumnIndex + 1, definitionColumnIndex + 1)) {
        const termCell = cells[termColumnIndex] ?? '';
        const defCell = cells[definitionColumnIndex] ?? '';

        const { text: termTextAfter, imageUrl: termImageInline } = extractImageFromMarkdown(termCell);
        const { text: defTextAfter, imageUrl: defImageInline } = extractImageFromMarkdown(defCell);

        let term_image_url: string | undefined = termImageInline;
        let definition_image_url: string | undefined = defImageInline;

        // 若存在专门的图片列，则优先使用专门列
        if (termImageColumnIndex !== -1 && cells.length > termImageColumnIndex) {
          const termImageCell = cells[termImageColumnIndex] ?? '';
          const { imageUrl } = extractImageFromMarkdown(termImageCell);
          if (imageUrl) term_image_url = imageUrl;
        }
        if (definitionImageColumnIndex !== -1 && cells.length > definitionImageColumnIndex) {
          const defImageCell = cells[definitionImageColumnIndex] ?? '';
          const { imageUrl } = extractImageFromMarkdown(defImageCell);
          if (imageUrl) definition_image_url = imageUrl;
        }

        const term = termTextAfter.trim();
        const definition = defTextAfter.trim();

        // 解析标签（如果有标签列）
        let tags: Tag[] = [];
        if (tagColumnIndex !== -1 && cells.length > tagColumnIndex) {
          const tagCell = cells[tagColumnIndex] ?? '';
          if (tagCell.trim()) {
            // 支持逗号和中文逗号分隔
            const tagNames = tagCell.split(/[,，]/).map(tag => tag.trim()).filter(tag => tag);
            // 这里需要将标签名称转换为Tag对象，暂时使用名称作为id
            tags = tagNames.map(name => ({ id: name, name, type: 'custom' as const }));
          }
        }

        if (term && definition) {
          result.push({ 
            term, 
            definition, 
            term_image_url: term_image_url, 
            definition_image_url: definition_image_url,
            tags: tags
          });
        }
      }
    }

    if (result.length === 0) {
      throw new Error('未找到有效的学习卡片数据');
    }

    return result;
  };

  // 处理批量导入
  const handleBatchImport = async () => {
    if (!id) return;
    
    try {
      setBatchImportError(null);
      setBatchImporting(true);
      
      if (!batchText.trim()) {
        setBatchImportError('请输入 Markdown 表格内容');
        return;
      }

      const newCards = parseMarkdownTable(batchText);
      // 显示自定义确认按钮，等待用户选择“替换”或“追加”
      setPendingImportCards(newCards);
      setPendingImportCount(newCards.length);
      
    } catch (err: any) {
      setBatchImportError(err.response?.data?.error || err.message || '批量导入失败');
    } finally {
      setBatchImporting(false);
    }
  };

  // 确认执行批量导入
  const confirmExecuteBatchImport = async (replaceAll: boolean) => {
    if (!id || !pendingImportCards) return;
    try {
      setBatchImportError(null);
      setBatchImporting(true);
      await apiClient.createCardsBatch(id, {
        cards: pendingImportCards.map(card => ({
          term: card.term,
          definition: card.definition,
          term_image_url: card.term_image_url,
          definition_image_url: card.definition_image_url,
          tag_ids: card.tags?.map(tag => tag.id) || []
        })),
        replace_all: replaceAll
      });
      await loadStudySet(id);
      // 重置并关闭
      setShowBatchImport(false);
      setBatchText('');
      setPendingImportCards(null);
      setPendingImportCount(null);
    } catch (err: any) {
      const errorData = err.response?.data;
      if (errorData?.error_type === 'missing_tags' && errorData?.missing_tags) {
        // 显示标签缺失错误，提供一键创建功能
        setMissingTags(errorData.missing_tags);
        setBatchImportError(null);
      } else {
        setBatchImportError(errorData?.error || '批量导入失败');
      }
    } finally {
      setBatchImporting(false);
    }
  };

  // 创建缺失的标签
  const createMissingTag = async (tagName: string) => {
    try {
      setCreatingTags(prev => new Set(prev).add(tagName));
      await apiClient.createCustomTag(tagName);
      
      // 清除自定义标签缓存，强制刷新
      clearTagCache('custom');
      
      // 从缺失标签列表中移除
      setMissingTags(prev => prev.filter(name => name !== tagName));
      
      // 重新尝试批量导入
      if (missingTags.length === 1 && pendingImportCards) {
        // 如果这是最后一个缺失的标签，自动重试导入
        setTimeout(() => {
          confirmExecuteBatchImport(false);
        }, 500);
      }
    } catch (err: any) {
      console.error('创建标签失败:', err);
      setBatchImportError('创建标签失败: ' + (err.response?.data?.error || err.message));
    } finally {
      setCreatingTags(prev => {
        const newSet = new Set(prev);
        newSet.delete(tagName);
        return newSet;
      });
    }
  };

  // 创建所有缺失的标签
  const createAllMissingTags = async () => {
    try {
      setCreatingTags(new Set(missingTags));
      
      // 批量创建标签
      const promises = missingTags.map(tagName => apiClient.createCustomTag(tagName));
      await Promise.all(promises);
      
      // 清除自定义标签缓存，强制刷新
      clearTagCache('custom');
      
      // 清空缺失标签列表
      setMissingTags([]);
      
      // 自动重试批量导入
      if (pendingImportCards) {
        setTimeout(() => {
          confirmExecuteBatchImport(false);
        }, 500);
      }
    } catch (err: any) {
      console.error('批量创建标签失败:', err);
      setBatchImportError('批量创建标签失败: ' + (err.response?.data?.error || err.message));
    } finally {
      setCreatingTags(new Set());
    }
  };

  // 清理函数
  useEffect(() => {
    return () => {
      if (infoSaveTimeoutRef.current) {
        clearTimeout(infoSaveTimeoutRef.current);
      }
    };
  }, []);

  // 检查是否有未保存的更改
  const hasUnsavedChanges = () => {
    return infoChanged || 
           flashcards.some(card => card.hasChanges || card.isNew);
  };

  // 页面离开前确认
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [infoChanged, flashcards]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Loading size="lg" text="加载学习集..." />
      </div>
    );
  }

  if (error && !studySetDetails) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {error}
          </h1>
          <button
            onClick={() => navigate('/dashboard')}
            className="btn-primary"
          >
            返回仪表盘
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 页面头部 */}
      <div className="mb-8">
        <button
          onClick={() => navigate(`/sets/${id}/view`)}
          className="flex items-center text-gray-600 hover:text-gray-800 mb-4 transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          返回查看页面
        </button>
        
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          编辑学习集
        </h1>
        <p className="text-gray-600">
          修改学习集的信息和内容。
        </p>
      </div>

      <div className="space-y-8">
        {/* 错误提示 */}
        {error && (
          <div className="alert-danger">
            {error}
          </div>
        )}

        {/* 基本信息 */}
        <div className="card">
          <div className="card-header">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">基本信息</h2>
              <div className="flex items-center space-x-2">
                {infoSaving && (
                  <div className="flex items-center text-sm text-gray-500">
                    <InlineLoading size="sm" />
                    <span className="ml-1">保存中...</span>
                  </div>
                )}
                {infoSaveSuccess && (
                  <div className="flex items-center text-sm text-green-600">
                    <CheckIcon className="h-4 w-4 mr-1" />
                    <span>已保存</span>
                  </div>
                )}
                {infoChanged && !infoSaving && (
                  <button
                    type="button"
                    onClick={handleSaveInfo}
                    className="btn-outline text-sm"
                  >
                    <CloudArrowUpIcon className="h-4 w-4 mr-1" />
                    保存信息
                  </button>
                )}
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                标题 *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                required
                className="input"
                placeholder="例如：英语单词第一章"
                value={formData.title}
                onChange={handleFormChange}
                onCompositionStart={handleCompositionStart}
                onCompositionEnd={handleCompositionEnd}
                disabled={infoSaving}
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                描述（可选）
              </label>
              <textarea
                id="description"
                name="description"
                rows={3}
                className="textarea"
                placeholder="简单描述这个学习集的内容..."
                value={formData.description}
                onChange={handleFormChange}
                onCompositionStart={handleCompositionStart}
                onCompositionEnd={handleCompositionEnd}
                disabled={infoSaving}
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_public"
                name="is_public"
                checked={formData.is_public}
                onChange={handleFormChange}
                disabled={infoSaving}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="is_public" className="ml-2 flex items-center text-sm text-gray-700">
                {formData.is_public ? (
                  <EyeIcon className="h-4 w-4 mr-1" />
                ) : (
                  <EyeSlashIcon className="h-4 w-4 mr-1" />
                )}
                公开学习集（其他人可以查看和学习）
              </label>
            </div>

            {/* 标签编辑器 */}
            <div className="pt-2 border-t border-gray-200">
              <TagEditor
                setId={id}
                initialTags={currentTags}
                onTagsChange={handleTagsChange}
                disabled={infoSaving}
              />
            </div>
            
            {/* 自动保存提示 */}
            <div className="text-xs text-gray-500 mt-2">
              💡 修改后会在停止输入5秒后自动保存（输入法完成后开始计时），或点击"保存信息"按钮立即保存
            </div>
          </div>
        </div>

        {/* 抽认卡 */}
        <div className="card">
          <div className="card-header">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">
                抽认卡 ({flashcards.length})
              </h2>
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={() => setShowBatchImport(true)}
                  className="btn-outline text-sm"
                  disabled={batchImporting}
                >
                  <DocumentTextIcon className="h-4 w-4 mr-1" />
                  批量导入
                </button>
                <button
                  type="button"
                  onClick={addFlashcard}
                  className="btn-outline text-sm"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  添加卡片
                </button>
                {flashcards.some(card => card.hasChanges || card.isNew) && (
                  <button
                    type="button"
                    onClick={saveAllCards}
                    className="btn-primary text-sm"
                  >
                    <CloudArrowUpIcon className="h-4 w-4 mr-1" />
                    批量保存
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* 批量导入模态窗口 */}
          {showBatchImport && (
            <div className="mb-6 border-2 border-dashed border-primary-300 rounded-lg p-6 bg-primary-50">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    <DocumentTextIcon className="h-5 w-5 inline mr-2" />
                    批量导入学习卡片
                  </h3>
                  <p className="text-sm text-gray-600">
                    粘贴 Markdown 表格格式的学习内容，系统将自动解析为学习卡片
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowBatchImport(false);
                    setBatchText('');
                    setBatchImportError(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              {/* 示例格式 */}
              <div className="mb-4 p-3 bg-white rounded border">
                <p className="text-sm font-medium text-gray-700 mb-2">示例格式（支持多种表头）：</p>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">标准格式：</p>
                    <pre className="text-xs text-gray-600 font-mono">
{`| 术语 | 定义 |
| --- | --- |
| Apple | 苹果，一种水果 |
| Book | 书籍，用于阅读的物品 |`}
                    </pre>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">颠倒顺序：</p>
                    <pre className="text-xs text-gray-600 font-mono">
{`| 定义 | 术语 |
| --- | --- |
| 苹果，一种水果 | Apple |
| 书籍，用于阅读的物品 | Book |`}
                    </pre>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">英文表头：</p>
                    <pre className="text-xs text-gray-600 font-mono">
{`| Term | Definition |
| --- | --- |
| Hello | 你好 |
| World | 世界 |`}
                    </pre>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">问答格式：</p>
                    <pre className="text-xs text-gray-600 font-mono">
{`| 问题 | 答案 |
| --- | --- |
| 1+1等于几？ | 2 |
| 中国首都是哪里？ | 北京 |`}
                    </pre>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">带标签格式：</p>
                    <pre className="text-xs text-gray-600 font-mono">
{`| 正面 | 反面 | 标签 |
| :---- | :---- | :---- |
| 在数物体的时候，用来表达物体个数的1, 2, 3……叫做{{c1::自然数}}。 | 自然数 | 数学,1年级,实数 |
| 2的平方根是{{c1::√2}} | √2 | 数学,初中,无理数 |`}
                    </pre>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  支持的关键词：术语/单词/词汇/term/word/vocabulary/题目/问题/question，定义/解释/答案/definition/explanation/answer/含义/meaning/翻译/translation，标签/tag/tags/分类/category
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  填空格式：使用 <code>{`{{c1::答案}}`}</code> 在正面创建填空题，标签用逗号分隔
                </p>
              </div>

              {/* 输入区域 */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  粘贴 Markdown 表格内容：
                </label>
                <textarea
                  className="w-full h-40 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
                  placeholder="在此粘贴 Markdown 表格..."
                  value={batchText}
                  onChange={(e) => setBatchText(e.target.value)}
                />
              </div>

              {/* 错误提示 */}
              {batchImportError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">
                    <i className="fas fa-exclamation-triangle mr-1"></i>
                    {batchImportError}
                  </p>
                </div>
              )}

              {/* 缺失标签提示 */}
              {missingTags.length > 0 && (
                <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3 flex-1">
                      <h3 className="text-sm font-medium text-yellow-800">
                        以下标签不存在，请先创建：
                      </h3>
                      <div className="mt-2">
                        <div className="flex flex-wrap gap-2 mb-3">
                          {missingTags.map(tagName => (
                            <button
                              key={tagName}
                              onClick={() => createMissingTag(tagName)}
                              disabled={creatingTags.has(tagName) || batchImporting}
                              className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              {creatingTags.has(tagName) ? (
                                <>
                                  <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-yellow-600" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  创建中...
                                </>
                              ) : (
                                <>
                                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                  </svg>
                                  {tagName}
                                </>
                              )}
                            </button>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={createAllMissingTags}
                            disabled={creatingTags.size > 0 || batchImporting}
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-yellow-800 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {creatingTags.size > 0 ? (
                              <>
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-yellow-600" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                批量创建中...
                              </>
                            ) : (
                              <>
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                                一键创建所有标签
                              </>
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => setMissingTags([])}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 transition-colors"
                          >
                            取消
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 选择替换或追加 */}
              {pendingImportCount !== null && (
                <div className="mb-4 p-3 bg-white border border-gray-200 rounded">
                  <p className="text-sm text-gray-700 mb-3">
                    解析到 {pendingImportCount} 张新卡片，请选择导入方式：
                  </p>
                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={() => confirmExecuteBatchImport(true)}
                      className="btn-primary text-sm"
                      disabled={batchImporting}
                      title="替换现有所有卡片"
                    >
                      替换
                    </button>
                    <button
                      type="button"
                      onClick={() => confirmExecuteBatchImport(false)}
                      className="btn-outline text-sm"
                      disabled={batchImporting}
                      title="追加到现有卡片后"
                    >
                      追加
                    </button>
                  </div>
                </div>
              )}

              {/* 操作按钮 */}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowBatchImport(false);
                    setBatchText('');
                    setBatchImportError(null);
                    setPendingImportCards(null);
                    setPendingImportCount(null);
                  }}
                  className="btn-outline text-sm"
                >
                  取消
                </button>
                <button
                  type="button"
                  onClick={handleBatchImport}
                  className="btn-primary text-sm"
                  disabled={!batchText.trim() || batchImporting}
                >
                  {batchImporting ? (
                    <div className="flex items-center">
                      <InlineLoading size="sm" />
                      <span className="ml-1">导入中...</span>
                    </div>
                  ) : (
                    <>
                      <CheckIcon className="h-4 w-4 mr-1" />
                      解析表格
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {[...flashcards].slice().reverse().map((card, index) => {
              const originalIndex = flashcards.length - 1 - index;
              return (
              <div key={card.id} className={`border rounded-lg p-4 ${
                card.isNew ? 'border-blue-300 bg-blue-50' : 
                card.hasChanges ? 'border-yellow-300 bg-yellow-50' : 
                'border-gray-200'
              }`}>
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-500">
                      卡片 #{index + 1}
                    </span>
                    {card.isNew && (
                      <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">
                        新卡片
                      </span>
                    )}
                    {card.hasChanges && !card.isNew && (
                      <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded">
                        有修改
                      </span>
                    )}
                    {card.saving && (
                      <div className="flex items-center text-xs text-gray-500">
                        <InlineLoading size="sm" />
                        <span className="ml-1">保存中...</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    {(card.hasChanges || card.isNew) && card.term.trim() && card.definition.trim() && (
                      <button
                        type="button"
                        onClick={() => saveCard(originalIndex)}
                        className="text-green-600 hover:text-green-700 transition-colors"
                        disabled={card.saving}
                        title="保存这张卡片"
                      >
                        <CloudArrowUpIcon className="h-4 w-4" />
                      </button>
                    )}
                    {!(Boolean(card.term_image_url) || Boolean(card.definition_image_url)) && !card.showImages && (
                      <button
                        type="button"
                        onClick={() => setFlashcards(prev => prev.map((c, i) => i === originalIndex ? { ...c, showImages: true } : c))}
                        className="text-primary-600 hover:text-primary-700 transition-colors text-sm"
                        disabled={card.saving}
                        title="添加图片"
                      >
                        <span className="inline-flex items-center">
                          <PhotoIcon className="h-4 w-4 mr-1" /> 添加图片
                        </span>
                      </button>
                    )}
                    {flashcards.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeFlashcard(originalIndex)}
                        className="text-gray-400 hover:text-danger-600 transition-colors"
                        disabled={card.saving}
                        title="删除这张卡片"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        术语
                      </label>
                      <input
                        type="text"
                        className="input"
                        placeholder="输入术语..."
                        value={card.term}
                        onChange={(e) => handleFlashcardChange(originalIndex, 'term', e.target.value)}
                        disabled={card.saving}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        定义
                      </label>
                      <input
                        type="text"
                        className="input"
                        placeholder="输入定义..."
                        value={card.definition}
                        onChange={(e) => handleFlashcardChange(originalIndex, 'definition', e.target.value)}
                        disabled={card.saving}
                      />
                    </div>
                  </div>

                  {/* 卡片标签编辑器 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      卡片标签（可选）
                    </label>
                    <TagEditor
                      initialTags={card.tags || []}
                      onTagsChange={(tagIds, tags) => {
                        // 使用传递的完整标签信息，如果没有则回退到原有逻辑
                        if (tags && tags.length > 0) {
                          handleFlashcardChange(originalIndex, 'tags', tags);
                        } else {
                          // 回退逻辑：将 tagIds 转换为 Tag 对象
                          const fallbackTags = tagIds.map(id => {
                            // 首先从当前卡片的标签中查找
                            const existingTag = (card.tags || []).find((tag: Tag) => tag.id === id);
                            if (existingTag) return existingTag;
                            
                            // 然后从学习集标签中查找
                            const setTag = currentTags.find((tag: Tag) => tag.id === id);
                            if (setTag) return setTag;
                            
                            // 如果都找不到，说明是新创建的自定义标签，使用 ID 作为名称
                            return { id, name: id, type: 'custom' as const };
                          });
                          handleFlashcardChange(originalIndex, 'tags', fallbackTags);
                        }
                      }}
                      disabled={card.saving}
                    />
                  </div>

                  {/* 图片录入区折叠/展开 */}
                  {(() => {
                    const hasAnyImage = Boolean(card.term_image_url) || Boolean(card.definition_image_url);
                    const expanded = hasAnyImage || card.showImages === true;
                    return (
                      <div>
                        {!hasAnyImage && (
                          <div className="flex items-center">
                            
                          </div>
                        )}
                        {expanded && (
                          <div className="grid md:grid-cols-2 gap-4 mt-3">
                            <ImageInput
                              label="术语图片（可选）"
                              imageUrl={card.term_image_url}
                              onImageChange={(url) => handleFlashcardChange(originalIndex, 'term_image_url', url)}
                              disabled={card.saving}
                            />
                            <ImageInput
                              label="定义图片（可选）"
                              imageUrl={card.definition_image_url}
                              onImageChange={(url) => handleFlashcardChange(originalIndex, 'definition_image_url', url)}
                              disabled={card.saving}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
              );
            })}
          </div>
        </div>

        {/* 操作按钮 */}
        <div className="flex justify-between items-center">
          <button
            type="button"
            onClick={() => {
              if (hasUnsavedChanges()) {
                const shouldLeave = window.confirm('您有未保存的更改，确定要离开吗？');
                if (shouldLeave) {
                  navigate(`/sets/${id}/view`);
                }
              } else {
                navigate(`/sets/${id}/view`);
              }
            }}
            className="btn-outline"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            返回查看页面
          </button>
          
          <div className="flex items-center space-x-4">
            {hasUnsavedChanges() && (
              <div className="flex items-center text-sm text-yellow-600">
                <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                <span>有未保存的更改</span>
              </div>
            )}
            
            {flashcards.filter(card => card.hasChanges || card.isNew).length > 0 && (
              <div className="text-sm text-gray-600">
                {flashcards.filter(card => card.hasChanges).length > 0 && 
                  `${flashcards.filter(card => card.hasChanges).length} 张卡片有修改`}
                {flashcards.filter(card => card.hasChanges).length > 0 && 
                 flashcards.filter(card => card.isNew).length > 0 && '，'}
                {flashcards.filter(card => card.isNew).length > 0 && 
                  `${flashcards.filter(card => card.isNew).length} 张新卡片`}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
